import { ReactComponent as ChevronBreadCrumbIcon } from 'assett/icons/chevron_breadcrumb_icon.svg';
import { ReactComponent as ChevronBreadCrumbIconTablet } from 'assett/icons/chevron_breadcrumb_icon_tablet.svg';
import useDevice from 'hooks/DesktopDevice.hook';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BreadcrumbsMui from 'styles/breadcrumbs/Breadcrumbs';
import Button from 'styles/button/Button';
import Typography from 'styles/typography/Typograpy';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';

interface Props {
	claimUuid: string;
	externalClaimId: string;
	id: string;
}

const Breadcrumbs = (props: Props) => {
	const location = useLocation();
	const navigate = useNavigate();
	const pathnames = location.pathname.split('/');
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { isDesktop } = useDevice();

	const { id, uuid, fraudId, complaintId } = useParams();

	const formatBreadcrumb = (route: string, index: number) => {
		// if we are inside the claims and the view is the single claim
		if (pathnames[1] === 'view-claims' && index > 1) {
			if (
				(index === 2 && pathnames.at(-1) === 'add-complaint') ||
				(index === 2 && pathnames.at(-1) === 'add-fraud')
			) {
				return `${tClientLabels('claim.breadcrumb')} #${props.externalClaimId}`;
			}

			if (index === 3 && pathnames.at(-1) === 'review-claim') {
				// we are inside the review claim path
				return `${tClientLabels('review.pageTitle')}`;
			}

			if (index === 3 && pathnames.at(-1) === 'add-complaint') {
				return `${tClientLabels('add-complaint.pageTitle')}`;
			}

			if (index === 3 && pathnames.at(-1) === 'add-fraud') {
				return `${tClientLabels('add-fraud.pageTitle')}`;
			}

			return route;
		}

		// if we are inside the complaints and the view is the single complaint
		if (
			pathnames[1] === 'view-complaint-tracking' &&
			index > 1 &&
			index === pathnames.length - 1
		) {
			return `${tClientLabels('complaint.breadcrumb')} #${complaintId}`;
		}

		// if we are inside the complaints and the view is the single complaint
		if (
			pathnames[1] === 'view-fraud-tracking' &&
			index > 1 &&
			index === pathnames.length - 1
		) {
			return `${tClientLabels('fraud.breadcrumb')} #${fraudId}`;
		}

		// none of the other cases
		return route;
	};

	const checkPathnames = (pathname: string) => {
		if (decodeURI(pathname) === props.claimUuid) {
			return `${tClientLabels('claim.breadcrumb')} #${props.externalClaimId}`;
		}

		if (
			decodeURI(pathname) === uuid &&
			pathnames[1] === 'view-settlement-letters'
		) {
			return '';
		}

		if (pathname === 'view-complaint' || pathname === 'view-fraud') {
			return '';
		}

		if (pathname === complaintId && pathnames[3] === 'view-complaint') {
			return `${tClientLabels('complaint.breadcrumb')} #${complaintId}`;
		}

		if (pathname === fraudId && pathnames[3] === 'view-fraud') {
			return `${tClientLabels('fraud.breadcrumb')} #${fraudId}`;
		}

		return decodeURI(pathname) === id
			? id
			: tClientLabels(`${pathname}.pageTitle`);
	};

	return (
		<BreadcrumbsMui
			sx={{ cursor: 'pointer', mt: 2.5, mb: 2.5 }}
			separator={
				isDesktop ? <ChevronBreadCrumbIcon /> : <ChevronBreadCrumbIconTablet />
			}
		>
			{pathnames
				.map(e => checkPathnames(e))
				.filter(route => route !== '')
				.map((route, index) => {
					if (
						index === 3 &&
						route === tClientLabels('assessment-fee-invoice.pageTitle')
					) {
						return null;
					}

					if (index === 0) {
						return (
							<Button
								variant="link"
								sx={{ minWidth: 0 }}
								onClick={() => navigate('/')}
								key={index}
							>
								<Typography variant="value3" sx={{ color: 'grey2.main' }}>
									{'Home'}
								</Typography>
							</Button>
						);
					}
					return (
						<Button
							variant="link"
							sx={{ minWidth: 0 }}
							onClick={() => {
								const path = [];

								for (let i = 1; i <= index; i++) {
									path.push(`/${pathnames[i]}`);
								}
								navigate(path.join(''));
							}}
							key={index}
						>
							<Typography
								variant="value3"
								sx={{
									color: index === pathnames.length - 1 ? 'primary.main' : 'grey2.main',
								}}
							>
								{formatBreadcrumb(route, index)}
							</Typography>
						</Button>
					);
				})}
		</BreadcrumbsMui>
	);
};

export default Breadcrumbs;
